import scraperApi from "api";
import { useState, useMemo, createContext, useContext, useEffect, useCallback, useRef } from "react";
import {
  makeTimestampDescComparator,
  makeNumberDescComparator,
  makeStatusComparator,
  makeStringAscComparator,
} from "pages/crawler/utils/crawlerSortUtils";
import type { CrawlerListItem } from "pages/crawler/components/jobs/types";
import { CRAWLERS_PAGE_SIZE, CRAWLER_POLL_INTERVAL } from "pages/crawler/constants/crawlerConstants";

// Create Context
const CrawlerContext = createContext<any>(undefined);

export const useCrawler = () => {
  const context = useContext(CrawlerContext);
  if (context === undefined) {
    throw new Error("useCrawler must be used within a CrawlerProvider");
  }
  return context;
};

// Helper function to filter crawlers by search query
const filterCrawlers = (crawlerList: CrawlerListItem[], query: string): CrawlerListItem[] => {
  if (!query.trim()) return crawlerList;
  return crawlerList.filter((crawler) =>
    (crawler.name || '').toLowerCase().includes(query.toLowerCase()) ||
    (crawler.domain || '').toLowerCase().includes(query.toLowerCase())
  );
};


export const CrawlerProvider = ({ children }: { children: React.ReactNode }) => {
  const [allCrawlers, setAllCrawlers] = useState<CrawlerListItem[]>([]);
  const [currentCrawler, setCurrentCrawler] = useState<CrawlerListItem | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countGracePeriod, setCountGracePeriod] = useState<number | null>(null);

  // Track the highest page loaded so polling can cover all accumulated data
  const maxPageLoadedRef = useRef(1);

  // crawler pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: CRAWLERS_PAGE_SIZE,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
  });

  // Derive filtered crawlers from allCrawlers + searchQuery
  const crawlers = useMemo(() => filterCrawlers(allCrawlers, searchQuery), [allCrawlers, searchQuery]);

  const fetchCrawlers = useCallback(async (params = { page: 1, limit: CRAWLERS_PAGE_SIZE }, append = false) => {
    setLoading(true);
    setError(null);

    try {
      const response = await scraperApi.crawlers.getAllCrawlers(params);

      if (response && response.data) {
        if (append && params.page > 1) {
          maxPageLoadedRef.current = Math.max(maxPageLoadedRef.current, params.page);
          setAllCrawlers((prev) => [...prev, ...(response.data.crawlers || [])]);
        } else {
          setAllCrawlers(response.data.crawlers || []);
        }

        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }

      if (response.data.countGracePeriod !== undefined) {
        setCountGracePeriod(response.data.countGracePeriod);
      }

      return response;
    } catch (err) {
      setError(`Failed to fetch crawlers: ${err}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMoreCrawlers = async () => {
    if (pagination.hasNextPage && !loading) {
      const nextPage = pagination.page + 1;
      await fetchCrawlers({ page: nextPage, limit: pagination.limit }, true);
    }
  };

  const refreshCrawlers = async () => {
    maxPageLoadedRef.current = 1;
    await fetchCrawlers({ page: 1, limit: pagination.limit }, false);
  };

  const clearError = () => setError(null);

  // Polling: fetches all pages the user has loaded so far in one request
  const pollCrawlers = useCallback(async () => {
    try {
      const limit = maxPageLoadedRef.current * CRAWLERS_PAGE_SIZE;
      const response = await scraperApi.crawlers.getAllCrawlers({ page: 1, limit });

      if (response?.data) {
        setAllCrawlers(response.data.crawlers || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
        if (response.data.countGracePeriod !== undefined) {
          setCountGracePeriod(response.data.countGracePeriod);
        }
      }
    } catch (err) {
      // Silent failure for polling — don't overwrite error state
      console.error('Polling error:', err);
    }
  }, []);

  // Conditional polling: only poll when there are active crawlers
  const hasActiveCrawlers = allCrawlers.some(
    (c) => c.hasRunningJobs || c.status?.state === 'running' || c.status?.state === 'scheduled'
  );

  useEffect(() => {
    if (!hasActiveCrawlers) return;

    const pollInterval = setInterval(pollCrawlers, CRAWLER_POLL_INTERVAL);
    return () => clearInterval(pollInterval);
  }, [hasActiveCrawlers, pollCrawlers]);

  // Comparators for sorting
  const compareLastRunDesc = makeTimestampDescComparator(
    ["lastFinishedAt", "lastStartedAt"],
    makeStringAscComparator("name")
  );

  const compareCreatedDesc = makeTimestampDescComparator(
    ["createdAt"],
    makeStringAscComparator("name")
  );

  const compareStatusDesc = makeStatusComparator(
    (item: CrawlerListItem) => item.status,
    compareLastRunDesc
  );

  const compareJobsDesc = makeNumberDescComparator("jobs");
  const compareCreditsDesc = makeNumberDescComparator("creditsSpent");

  const crawlerComparators: Record<string, (a: CrawlerListItem, b: CrawlerListItem) => number> = {
    lastRun: compareLastRunDesc,
    name: makeStringAscComparator("name"),
    domain: makeStringAscComparator("domain"),
    url: makeStringAscComparator("domain"),
    jobs: compareJobsDesc,
    creditsSpent: compareCreditsDesc,
    status: compareStatusDesc,
    created: compareCreatedDesc,
  };

  const sortCrawlerTableData = (sortBy: string) => {
    const comparator = crawlerComparators[sortBy] ?? crawlerComparators.lastRun;
    setAllCrawlers((prev) => [...prev].sort(comparator));
  };

  const searchCrawlers = (query: string) => {
    setSearchQuery(query);
  };

  // Initial fetch on mount
  useEffect(() => {
    fetchCrawlers({ page: 1, limit: CRAWLERS_PAGE_SIZE });
  }, [fetchCrawlers]);

  return (
    <CrawlerContext.Provider
      value={{
        // State
        crawlers,
        searchQuery,
        currentCrawler,
        loading,
        error,
        pagination,
        hasActiveCrawlers,
        countGracePeriod,

        // Actions
        fetchCrawlers,
        loadMoreCrawlers,
        refreshCrawlers,
        setCurrentCrawler,
        searchCrawlers,
        clearError,

        // sortData
        sortCrawlerTableData,
      }}
    >
      {children}
    </CrawlerContext.Provider>
  );
};
